import { ChevronDown, PawPrint } from 'lucide-react';
import { useState, type CSSProperties, type FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { useFittingCount } from '@/features/storefront/layouts/chrome/useFittingCount';
import { cn } from '@/lib/cn';

/** The masthead's navigation voice: small, bold capitals, lightly spaced. */
const PAWLY_NAV =
  'items-center gap-7 whitespace-nowrap text-[13px] font-semibold uppercase tracking-[0.06em]';

/** A nav link: a teal underline marks the one under the pointer. */
const NAV_LINK =
  'border-b-2 border-transparent pb-0.5 hover:border-[color:var(--storefront-primary)]';

const HEADING_FONT: CSSProperties = { fontFamily: 'var(--storefront-heading-font)' };

/**
 * The rail's ground: the primary teal rather than the text colour, by the
 * same variable swap Kinetic and Emeraude make. The rail sets its copy in the
 * page colour, so the result is cream type on teal - and a merchant who
 * repaints their primary gets a rail that follows it.
 */
const RAIL_STYLE = {
  '--storefront-palette-text': 'var(--storefront-palette-primary)',
} as CSSProperties;

/** Whether an item opens a whole panel of columns rather than a short list. */
function isMega(item: StorefrontMenuItem): boolean {
  return (item.items ?? []).some((child) => (child.items ?? []).length > 0);
}

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * An item whose children have children - "Shop" → Dogs → Dog food - opens a
 * **mega panel** the width of the page, one column per child with its links
 * under a tall capital heading, which is how a pet shop is browsed: by animal,
 * then by need. An item with a flat list of children opens a small rounded
 * card under itself. Either way the item stays a link. Opens on hover and on
 * focus, closes on Escape and when focus leaves.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const [open, setOpen] = useState(false);
  const children = item.items ?? [];
  const mega = isMega(item);

  if (children.length === 0) {
    return <MenuLink item={item} preview={preview} className={NAV_LINK} />;
  }

  return (
    <div
      className={cn('flex h-full items-center', !mega && 'relative')}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <span className="flex items-center gap-1">
        <MenuLink item={item} preview={preview} className={NAV_LINK} />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open && mega ? (
        // Positioned against the header (the nearest positioned ancestor), so
        // the panel spans the page rather than the item. Its bottom corners
        // take the theme's radius, like every tile on the page.
        <div
          className="absolute inset-x-0 top-full z-40 overflow-hidden border-t shadow-lg"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
            borderBottomLeftRadius: 'var(--storefront-radius)',
            borderBottomRightRadius: 'var(--storefront-radius)',
          }}
        >
          <StorefrontContainer className="grid grid-cols-2 gap-x-10 gap-y-8 py-10 md:grid-cols-4">
            {children.map((column) => (
              <div key={`${column.label}:${column.url}`}>
                <div style={HEADING_FONT}>
                  <MenuLink
                    item={column}
                    preview={preview}
                    className="mb-4 block text-3xl uppercase leading-none tracking-[0.01em] hover:opacity-70"
                  />
                </div>
                <div className="flex flex-col gap-3 text-[15px] font-normal normal-case tracking-normal">
                  {(column.items ?? []).map((link) => (
                    <MenuLink
                      key={`${link.label}:${link.url}`}
                      item={link}
                      preview={preview}
                      className="hover:text-[color:var(--storefront-primary)]"
                    />
                  ))}
                </div>
              </div>
            ))}
          </StorefrontContainer>
        </div>
      ) : null}

      {open && !mega ? (
        <div className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-2">
          <div
            className="min-w-52 overflow-hidden border py-3 font-normal normal-case tracking-normal shadow-lg"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
              borderRadius: 'calc(var(--storefront-radius) / 2)',
            }}
          >
            {children.map((child) => (
              <MenuLink
                key={`${child.label}:${child.url}`}
                item={child}
                preview={preview}
                className="block px-5 py-2 hover:text-[color:var(--storefront-primary)]"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "More": the menu items that did not fit on the bar, as a short card.
 * Opens like a flat-list `NavItem`; the items' own children are listed
 * indented under them.
 */
function MoreItem({ items, preview }: { items: StorefrontMenuItem[]; preview?: boolean }) {
  const copy = useShopContent();
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative flex h-full shrink-0 items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={cn('flex items-center gap-1 uppercase', NAV_LINK)}
      >
        {copy('header.more')}
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open ? (
        <div className="absolute end-0 top-full z-40 pt-2">
          <div
            className="min-w-52 overflow-hidden border py-3 font-normal normal-case tracking-normal shadow-lg"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
              borderRadius: 'calc(var(--storefront-radius) / 2)',
            }}
          >
            {items.map((item) => (
              <div key={`${item.label}:${item.url}`}>
                <MenuLink
                  item={item}
                  preview={preview}
                  className="block px-5 py-2 hover:text-[color:var(--storefront-primary)]"
                />
                {(item.items ?? []).map((child) => (
                  <MenuLink
                    key={`${child.label}:${child.url}`}
                    item={child}
                    preview={preview}
                    className="block py-1.5 ps-9 pe-5 text-[14px] hover:text-[color:var(--storefront-primary)]"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The merchant's menu on the bar, as many items as the column holds.
 *
 * The bar shares its row with the centred wordmark, and a long menu - or a
 * wide heading face on the wordmark - used to run the last items underneath
 * it. The items are measured in an invisible copy (`useFittingCount`, as the
 * category strip does) and what does not fit folds into "More".
 */
function FittingMenu({ items, preview, name }: { items: StorefrontMenuItem[]; preview?: boolean; name?: string }) {
  const copy = useShopContent();
  const { space, measure, count } = useFittingCount(items.length);
  const shown = items.slice(0, count);
  const rest = items.slice(shown.length);
  return (
    <div ref={space} className="relative flex min-w-0 flex-1">
      <nav aria-label={name} className={cn('flex', PAWLY_NAV)}>
        {shown.map((item) => (
          <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />
        ))}
        {rest.length > 0 ? <MoreItem items={rest} preview={preview} /> : null}
      </nav>
      <div
        ref={measure}
        aria-hidden
        className={cn(PAWLY_NAV, 'pointer-events-none invisible absolute top-0 left-0 flex w-max')}
      >
        {items.map((item) => (
          <span key={`${item.label}:${item.url}`} className="flex shrink-0 items-center gap-1">
            {item.label}
            {(item.items ?? []).length > 0 ? <span className="size-3.5" /> : null}
          </span>
        ))}
        <span className="flex shrink-0 items-center gap-1">
          {copy('header.more')}
          <span className="size-3.5" />
        </span>
      </div>
    </div>
  );
}

/**
 * A pet shop's masthead: the teal rail with the standing promises, then one
 * cream deck - the menu on the left in small capitals, the wordmark centred in
 * tall capitals with a tangerine paw after it, the shopper's corner right.
 *
 * Like the other families, the bar stands the shop's own shelves in when the
 * merchant has built no menu, and below the large breakpoint the menu moves
 * into the sheet behind the menu button and the wordmark moves left beside it.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn('relative border-b', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{
        backgroundColor: 'var(--storefront-background)',
        borderColor: 'var(--storefront-surface)',
      }}
    >
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <StorefrontContainer className="flex min-h-[4.75rem] items-stretch gap-2 sm:gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] preview-narrow:flex!">
        <div className="flex min-w-0 items-stretch">
          <MobileNav
            menu={menu}
            hiddenFrom="lg:hidden"
            preview={preview}
            categoryFallback
            className="self-center"
          />
          {/* The column's whole room, so the category strip below measures
              what it has rather than what it holds. */}
          <div className="hidden min-w-0 flex-1 lg:flex preview-narrow:hidden!">
            {items.length > 0 ? (
              <FittingMenu items={items} preview={preview} name={menu?.name} />
            ) : preview ? null : (
              <HeaderCategoryLinks
                className={cn('flex', PAWLY_NAV)}
                linkClassName={NAV_LINK}
                header={header}
                limit={5}
              />
            )}
          </div>
        </div>

        <div className="flex min-w-0 items-center justify-center">
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-2 sm:gap-2.5"
            nameClassName="text-[1.4rem] sm:text-[2.1rem] uppercase leading-none tracking-[0.01em]"
            nameStyle={HEADING_FONT}
            nameSuffix={
              <PawPrint
                className="ms-1 inline size-[0.7em] -translate-y-[0.1em]"
                style={{ color: 'var(--storefront-accent)' }}
                fill="currentColor"
                strokeWidth={0}
              />
            }
          />
        </div>

        <div className="ms-auto flex shrink-0 items-center justify-end gap-2.5 sm:gap-5">
          {endSlot}
        </div>
      </StorefrontContainer>
    </header>
  );
};
